import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Save, Building2, Trash2, ExternalLink, Users, FolderOpen, Briefcase, Mail } from 'lucide-react';
import { toast } from 'sonner';
import DeleteConfirmModal from '../../components/DeleteConfirmModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanyDetailPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [data, setData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'partner',
    subscription_status: 'trial'
  });

  useEffect(() => {
    fetchCompanyDetail();
  }, [companyId]);

  const fetchCompanyDetail = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
      const company = response.data.company || response.data;
      setFormData({
        name: company.name || '',
        type: company.type || 'partner',
        subscription_status: company.subscription_status || 'trial'
      });
    } catch (error) {
      toast.error('Failed to load company details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    setSaving(true);
    try {
      await axios.put(`${API}/admin/companies/${companyId}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Company updated successfully');
      fetchCompanyDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update company');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    const token = localStorage.getItem('token');
    
    try {
      await axios.delete(`${API}/admin/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { reason: 'Deleted from admin panel' }
      });
      toast.success('Company deleted successfully');
      navigate('/admin/companies');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete company');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#62D84E' }}></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-8">
        <p className="text-red-600">Company not found</p>
      </div>
    );
  }

  const company = data.company || data;
  const stats = data.stats || {};
  const users = data.users || [];
  const resources = data.resources || [];
  const projects = data.projects || [];

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <Button variant="ghost" onClick={() => navigate('/admin/companies')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Companies
          </Button>
          <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>Company Details</h1>
          <p className="text-gray-600 mt-2">View and edit company information</p>
        </div>
        <div className="flex gap-2">
          <a 
            href={`${window.location.origin}/company/${companyId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline">
              <ExternalLink className="w-4 h-4 mr-2" />
              View Public Page
            </Button>
          </a>
          <Button variant="outline" onClick={() => setShowDeleteModal(true)} className="text-red-600">
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Company
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Company"
        message={`Are you sure you want to delete ${company.name}?`}
        entityName={company.name}
        consequences={[
          `${stats.total_users || 0} users will lose access`,
          `${stats.total_resources || 0} resources will be affected`,
          `${stats.total_projects || 0} projects will be affected`
        ]}
        isDeleting={deleting}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Users</p>
                <p className="text-2xl font-bold">{stats.total_users || 0}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Resources</p>
                <p className="text-2xl font-bold">{stats.total_resources || 0}</p>
              </div>
              <FolderOpen className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Projects</p>
                <p className="text-2xl font-bold">{stats.total_projects || 0}</p>
              </div>
              <Briefcase className="w-8 h-8 text-orange-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Profile</p>
                <p className="text-2xl font-bold">{stats.profile_completion || 0}%</p>
              </div>
              <Building2 className="w-8 h-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Company Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Building2 className="w-5 h-5 mr-2" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Company Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            
            <div>
              <Label htmlFor="type">Company Type</Label>
              <select
                id="type"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="partner">Partner</option>
                <option value="freelance">Freelance</option>
              </select>
            </div>

            <div>
              <Label htmlFor="subscription">Subscription Status</Label>
              <select
                id="subscription"
                value={formData.subscription_status}
                onChange={(e) => setFormData({ ...formData, subscription_status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="trial">Trial</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <Button onClick={handleSave} disabled={saving} style={{ backgroundColor: '#62D84E' }} className="w-full">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Statistics & Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-gray-600">Company ID</Label>
              <p className="text-sm font-mono bg-gray-100 p-2 rounded">{company.id}</p>
            </div>
            
            <div>
              <Label className="text-gray-600">Total Users</Label>
              <p className="text-sm font-semibold">{stats.total_users || 0}</p>
            </div>

            <div>
              <Label className="text-gray-600">Total Resources</Label>
              <p className="text-sm font-semibold">{stats.total_resources || 0}</p>
            </div>

            <div>
              <Label className="text-gray-600">Total Projects</Label>
              <p className="text-sm font-semibold">{stats.total_projects || 0}</p>
            </div>

            <div>
              <Label className="text-gray-600">Reservations Sent</Label>
              <p className="text-sm">{stats.reservations_sent || 0}</p>
            </div>

            <div>
              <Label className="text-gray-600">Reservations Received</Label>
              <p className="text-sm">{stats.reservations_received || 0}</p>
            </div>

            <div>
              <Label className="text-gray-600">Total Collaborations</Label>
              <p className="text-sm">{stats.total_collaborations || 0}</p>
            </div>

            <div>
              <Label className="text-gray-600">Created At</Label>
              <p className="text-sm">{company.created_at ? new Date(company.created_at).toLocaleString() : 'N/A'}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users List */}
      {users.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Company Users</CardTitle>
            <CardDescription>{users.length} user(s) in this company</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Role</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.map((user, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{user.email}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline">{user.role?.toUpperCase()}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <a href={`mailto:${user.email}`}>
                        <Button size="sm" variant="ghost">
                          <Mail className="w-4 h-4" />
                        </Button>
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Resources List */}
      {resources.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Company Resources</CardTitle>
            <CardDescription>{resources.length} resource(s) in this company</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Role</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {resources.map((res, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium">
                      {res.first_name} {res.last_name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{res.role}</td>
                    <td className="px-4 py-3">
                      <Badge className={res.is_activated ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {res.availability_status || 'N/A'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CompanyDetailPage;
